import { useAuth } from "context/AuthContext";
import {
  CalendarClock,
  ClipboardCheck,
  FileStack,
  Landmark,
  RefreshCw,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DateView } from "utils/helper";
import {
  loadModuleCount,
  MODULE_ROUTES,
  ModuleCount,
  ModuleKey,
  visibleModules,
} from "./dashboardLists";
import { CountBars, CountUp, Donut, Slice } from "./fragment/DashboardCharts";

type ModuleState =
  | { state: "loading" }
  | { state: "ok"; data: ModuleCount }
  | { state: "error" };

const MODULE_COLORS: Record<ModuleKey, string> = {
  incidents: "#F59E0B",
  investigationFiles: "#AA232F",
  furtherInvestigation: "#D97706",
  charges: "#DC2626",
  upcomingHearings: "#0A1D39",
  prosecutorAppeal: "#9D0303",
  cassationAppeal: "#7F1D1D",
  defendantAppeal: "#212121",
  defendantCassation: "#414040",
  bailRight: "#16A34A",
  witnessProtection: "#2563EB",
  schedules: "#747171",
  assetRecovery: "#B45309",
  assetAppeal: "#92400E",
  assetAppealRespondent: "#78350F",
};

// Events rather than cases: shown in their own lists, not in the workload chart.
const EVENT_MODULES: ModuleKey[] = ["upcomingHearings", "schedules"];
// Lists loaded with a single request. They are started first: the browser
// only sends a few requests to one server at a time, and the other lists
// send one request per status tab.
const SINGLE_REQUEST_MODULES: ModuleKey[] = [
  "bailRight",
  "witnessProtection",
  "schedules",
  "furtherInvestigation",
  "assetRecovery",
  "assetAppeal",
  "assetAppealRespondent",
  "upcomingHearings",
];
// A list that has not answered by then shows "—" instead of loading forever.
const MODULE_TIMEOUT_MS = 60000;

const withTimeout = <T,>(promise: Promise<T>) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), MODULE_TIMEOUT_MS);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

// The module highlighted first for a role, in this order of preference.
const MAIN_MODULES: ModuleKey[] = [
  "investigationFiles",
  "charges",
  "assetRecovery",
  "prosecutorAppeal",
  "defendantAppeal",
];

// Further large cards after the main one, in this order of preference; the
// other lists are shown as compact chips.
const TOP_CARD_ORDER: ModuleKey[] = [
  "charges",
  "upcomingHearings",
  "prosecutorAppeal",
  "assetRecovery",
  "bailRight",
  "incidents",
  "investigationFiles",
  "furtherInvestigation",
  "defendantAppeal",
  "witnessProtection",
  "cassationAppeal",
  "defendantCassation",
  "assetAppeal",
  "assetAppealRespondent",
];
const TOP_CARDS = 5;

// Status tabs that are the user's own queue, by role family: a coordinator
// acts on what is forwarded to the coordinator, and so on.
const normalizeStatus = (key: string) => key.toLowerCase().replace(/[\s-]+/g, "_");
const actionMatchers = (roles: string[] = []) => {
  const text = roles.join(" ").toLowerCase();
  const matchers: ((status: string) => boolean)[] = [];
  if (/coordinator|director|deputy|generalattorney/.test(text)) {
    matchers.push(
      (k) => k.includes("coordinator") || k.includes("cordinator") || k === "new_request",
    );
  }
  if (/regist/.test(text)) matchers.push((k) => k.includes("registrar"));
  if (/prosecutor/.test(text)) matchers.push((k) => k.includes("assigned_to_prosecutor"));
  if (/clerk/.test(text)) matchers.push((k) => k.includes("clerk"));
  return (status: string) => matchers.some((match) => match(normalizeStatus(status)));
};

const Card: React.FC<{
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <section className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow">
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {action}
    </div>
    <div className="flex-1">{children}</div>
  </section>
);

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { loginData } = useAuth();

  const [modules, setModules] = useState<ModuleKey[]>([]);
  const [results, setResults] = useState<Partial<Record<ModuleKey, ModuleState>>>({});
  const [refreshing, setRefreshing] = useState(false);

  const moduleLabel = useCallback(
    (key: ModuleKey) =>
      ({
        incidents: t("incident"),
        investigationFiles: t("investigation_file"),
        furtherInvestigation: t("further_investigation"),
        charges: t("charge"),
        upcomingHearings: t("upcoming_hearings"),
        prosecutorAppeal: t("prosecutor_appeal"),
        cassationAppeal: t("cassation"),
        defendantAppeal: t("defendant_appeal"),
        defendantCassation: t("defendant_cassation"),
        bailRight: t("bail_right"),
        witnessProtection: t("sidebar_witness_protection"),
        schedules: t("sidebar_schedule"),
        assetRecovery: t("asset_recovery"),
        assetAppeal: `${t("asset_appeal")} · ${t("prosecutor_appeal")}`,
        assetAppealRespondent: `${t("asset_appeal")} · ${t("respondent_appeal")}`,
      })[key],
    [t],
  );

  const load = useCallback(() => {
    if (!loginData) return;
    const keys = visibleModules(loginData).filter((k) => k !== "schedules");
    setModules(keys);
    setResults(Object.fromEntries(keys.map((k) => [k, { state: "loading" }])));
    setRefreshing(true);
    const startOrder = [
      ...keys.filter((k) => SINGLE_REQUEST_MODULES.includes(k)),
      ...keys.filter((k) => !SINGLE_REQUEST_MODULES.includes(k)),
    ];
    Promise.all(
      startOrder.map((key) =>
        withTimeout(loadModuleCount(key, loginData))
          .then((data) => setResults((r) => ({ ...r, [key]: { state: "ok", data } })))
          .catch(() => setResults((r) => ({ ...r, [key]: { state: "error" } }))),
      ),
    ).finally(() => setRefreshing(false));
  }, [loginData]);

  useEffect(() => {
    load();
  }, [load]);

  const dataOf = (key: ModuleKey) => {
    const result = results[key];
    return result?.state === "ok" ? result.data : undefined;
  };
  const statusLabel = (s: { label: string; labelKey?: string }) =>
    s.labelKey ? t(s.labelKey, { defaultValue: s.label }) : s.label;

  const caseModules = modules.filter((k) => !EVENT_MODULES.includes(k));
  const main = MAIN_MODULES.find((k) => caseModules.includes(k)) ?? caseModules[0];
  // Status charts only for lists that have status tabs for this role.
  const withStatus = caseModules.filter((k) => (dataOf(k)?.byStatus.length ?? 0) > 0);
  const [statusFirst, statusSecond] = withStatus;
  const topCards = main
    ? [main, ...TOP_CARD_ORDER.filter((k) => k !== main && modules.includes(k))].slice(0, TOP_CARDS)
    : [];
  const chips = modules.filter((k) => !topCards.includes(k));

  const isWaiting = actionMatchers(loginData?.roles);
  const waiting = modules
    .flatMap((key) =>
      (dataOf(key)?.byStatus ?? [])
        .filter((s) => s.count > 0 && isWaiting(s.key))
        .map((s) => ({ key, status: s, route: MODULE_ROUTES[key] })),
    )
    .sort((a, b) => b.status.count - a.status.count);
  const stillLoading = modules.some((k) => results[k]?.state === "loading");
  const unitName = loginData?.structure?.admin_unit?.unit_name;
  const roleName = loginData?.roles?.[1] ?? loginData?.roles?.[0];

  const slices: Slice[] = caseModules
    .map((key) => ({
      key,
      label: moduleLabel(key),
      value: dataOf(key)?.total ?? 0,
      color: MODULE_COLORS[key],
    }))
    .filter((s) => s.value > 0);

  const viewAll = (key: ModuleKey) => (
    <Link to={MODULE_ROUTES[key]} className="text-sm font-medium text-[#AA232F] hover:underline">
      {t("pd_view_all")}
    </Link>
  );

  const countOf = (key: ModuleKey) => {
    const result = results[key];
    if (!result || result.state === "loading") {
      return <span className="inline-block h-7 w-12 animate-pulse rounded bg-[#F2F2F2]" />;
    }
    if (result.state === "error") {
      return (
        <span className="text-base font-medium text-[#747171]" title={t("pd_load_failed")}>
          —
        </span>
      );
    }
    return <CountUp value={result.data.total} />;
  };

  const recentList = (key: ModuleKey | undefined, icon: React.ReactNode) => {
    const data = key ? dataOf(key) : undefined;
    if (!key) return null;
    return (
      <Card
        title={
          key === "upcomingHearings"
            ? t("upcoming_hearings")
            : `${t("pd_recent")}: ${moduleLabel(key)}`
        }
        action={viewAll(key)}
      >
        {data?.recent.length ? (
          <ul className="divide-y divide-gray-100">
            {data.recent.map((row) => (
              <li key={`${row.id}-${row.date ?? ""}`}>
                <Link to={row.route} className="flex items-center gap-3 py-3 hover:bg-gray-50">
                  <span className="rounded-lg bg-[#FDEBEC] p-2 text-[#AA232F]">{icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {row.title || "—"}
                    </span>
                    {row.subtitle && (
                      <span className="block truncate text-xs text-[#747171]">{row.subtitle}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-right">
                    {row.status && (
                      <span className="block rounded-full border border-gray-200 bg-[#F2F2F2] px-2 py-0.5 text-xs text-gray-700">
                        {t(row.status, { defaultValue: row.status })}
                      </span>
                    )}
                    {row.date && (
                      <span className="mt-1 block text-xs text-[#747171]">{DateView(row.date)}</span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-8 text-center text-sm text-[#747171]">
            {results[key]?.state === "loading" ? t("loading") : t("pd_no_data")}
          </p>
        )}
      </Card>
    );
  };

  return (
    <div className="max-w-screen ml-1 mr-4 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("pd_dashboard")}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t("pd_welcome_back")},{" "}
            <span className="font-semibold text-[#0A1D39]">{loginData?.full_name}</span>.{" "}
            {t("pd_overview")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {roleName && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FDEBEC] px-3 py-1 text-xs font-medium text-[#AA232F] ring-1 ring-[#AA232F]/20">
                <UserRound size={14} />
                {roleName}
              </span>
            )}
            {unitName && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F2F2F2] px-3 py-1 text-xs font-medium text-gray-800">
                <Landmark size={14} />
                {unitName}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-2 self-start rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 lg:self-auto"
        >
          <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          {t("refresh")}
        </button>
      </div>

      {!modules.length ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600 shadow">
          {t("pd_no_modules")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {topCards.map((key, i) => {
              const first = dataOf(key)?.byStatus[0];
              const highlighted = i === 0;
              return (
                <Link
                  key={key}
                  to={MODULE_ROUTES[key]}
                  className={`flex flex-col justify-between rounded-xl p-5 shadow transition ${
                    highlighted
                      ? "bg-[#AA232F] text-white hover:bg-[#9D0303]"
                      : "border border-gray-200 bg-white hover:shadow-md"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`shrink-0 rounded-full p-2.5 ${highlighted ? "bg-white/15" : ""}`}
                      style={
                        highlighted
                          ? undefined
                          : { backgroundColor: `${MODULE_COLORS[key]}1a`, color: MODULE_COLORS[key] }
                      }
                    >
                      {key === "upcomingHearings" ? <CalendarClock size={20} /> : <ShieldCheck size={20} />}
                    </span>
                    <div className="min-w-0">
                      <p
                        className={`truncate text-sm ${highlighted ? "text-red-100" : "text-gray-600"}`}
                        title={moduleLabel(key)}
                      >
                        {moduleLabel(key)}
                      </p>
                      <p className={`mt-1 text-3xl font-bold ${highlighted ? "" : "text-gray-900"}`}>
                        {countOf(key)}
                      </p>
                      <p className={`text-xs ${highlighted ? "text-red-100" : "text-[#747171]"}`}>
                        {t("pd_in_your_list")}
                      </p>
                    </div>
                  </div>
                  <p
                    className={`mt-4 truncate text-xs ${highlighted ? "text-red-100" : "text-[#747171]"}`}
                    title={first ? `${statusLabel(first)}: ${first.count}` : undefined}
                  >
                    {first ? `${statusLabel(first)}: ${first.count}` : " "}
                  </p>
                </Link>
              );
            })}
          </div>

          {chips.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {chips.map((key) => {
                const first = dataOf(key)?.byStatus[0];
                return (
                  <Link
                    key={key}
                    to={MODULE_ROUTES[key]}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow transition hover:shadow-md"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: MODULE_COLORS[key] }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-gray-900" title={moduleLabel(key)}>
                          {moduleLabel(key)}
                        </span>
                        {first && (
                          <span className="block truncate text-xs text-[#747171]">
                            {statusLabel(first)}: {first.count}
                          </span>
                        )}
                      </span>
                    </span>
                    <span
                      className="shrink-0 rounded-lg px-3 py-1 text-sm font-semibold"
                      style={{ backgroundColor: `${MODULE_COLORS[key]}1a`, color: MODULE_COLORS[key] }}
                    >
                      {countOf(key)}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          <Card
            title={
              <span className="flex items-center gap-2">
                <ClipboardCheck size={18} className="text-[#AA232F]" />
                {t("pd_waiting_for_you")}
                {waiting.length > 0 && (
                  <span className="rounded-full bg-[#AA232F] px-2 py-0.5 text-xs font-semibold text-white">
                    {waiting.reduce((sum, w) => sum + w.status.count, 0)}
                  </span>
                )}
              </span>
            }
          >
            {waiting.length ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {waiting.map(({ key, status, route }) => (
                  <Link
                    key={`${key}-${status.key}`}
                    to={route}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[#AA232F]/20 bg-[#FDEBEC] px-4 py-3 transition hover:border-[#AA232F]/50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-gray-900">
                        {moduleLabel(key)}
                      </span>
                      <span className="block truncate text-xs text-[#AA232F]">{statusLabel(status)}</span>
                    </span>
                    <span className="shrink-0 text-2xl font-bold text-[#AA232F]">
                      <CountUp value={status.count} />
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-[#747171]">
                {stillLoading ? t("loading") : t("pd_nothing_waiting")}
              </p>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card title={t("pd_workload_by_module")}>
              {slices.length ? (
                <Donut slices={slices} centerLabel={t("pd_items")} />
              ) : (
                <p className="py-8 text-center text-sm text-[#747171]">{t("pd_no_data")}</p>
              )}
            </Card>
            {[statusFirst, statusSecond].map((key) =>
              key ? (
                <Card
                  key={key}
                  title={`${moduleLabel(key)} — ${t("pd_by_status")}`}
                  action={viewAll(key)}
                >
                  <CountBars
                    rows={(dataOf(key)?.byStatus ?? []).map((s) => ({
                      key: s.key,
                      label: statusLabel(s),
                      count: s.count,
                    }))}
                    total={dataOf(key)?.total}
                    emptyText={t("pd_no_data")}
                  />
                </Card>
              ) : null,
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            {modules.includes("upcomingHearings") &&
              recentList("upcomingHearings", <CalendarClock size={18} />)}
            {recentList(main, <FileStack size={18} />)}
            {recentList(
              caseModules.find((k) => k !== main && (dataOf(k)?.recent.length ?? 0) > 0),
              <FileStack size={18} />,
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
